import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../context/ThemeContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'
import { motion, AnimatePresence } from 'framer-motion'

// --- Internal Step Components (Ported from Admin) ---
import Step1BasicInfo from '../admin/project-steps/Step1BasicInfo'
import Step2Location from '../admin/project-steps/Step2Location'
import Step3Developer from '../admin/project-steps/Step3Developer'
import Step4FloorPlan from '../admin/project-steps/Step4FloorPlan'
import StepSEOTimeline from '../admin/project-steps/StepSEOTimeline'
import Step8Media from '../admin/project-steps/Step7Media'
import Step8Grid from '../admin/project-steps/Step8Grid'
import Step10Offers from '../admin/project-steps/Step9Offers'
import StepContactInfo from '../admin/project-steps/StepContactInfo'

// --- Types & Constants ---
import { 
    ProjectForm, 
    FloorPlan, 
    AdditionalDetail, 
    Specification, 
    LegalDocument, 
    FAQ, 
    NearbyPlace, 
    InventoryItem,
    GalleryCategory,
    GalleryState,
    GalleryFileState
} from '../admin/project-steps/types'
import { 
    SUBCATEGORIES, 
    NEARBY_CATEGORY_ICONS, 
    DEFAULT_CONSTRUCTION_STATUS_OPTIONS 
} from '../admin/project-steps/constants'
const BROKER_STEPS = [
    'Basic Details',
    'Location Details',
    'Developer Info',
    'Floor Variants (Config)',
    'SEO Details',
    'Gallery & Documents',
    'Content & Highlights',
    'Offers & Pricing',
    'Contact Info'
];

export default function BrokerAddProject() {
    const { token } = useAuth()
    const { theme } = useTheme()
    const navigate = useNavigate()
    const { id } = useParams()
    const [currentStep, setCurrentStep] = useState(1)
    const [activeStep4Tab, setActiveStep4Tab] = useState<'2d' | '3d' | null>('2d')
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [errors, setErrors] = useState<Record<string, boolean>>({})
    const [activeProjectId, setActiveProjectId] = useState<string | null>(id || null)
    const [userProfile, setUserProfile] = useState<any>(null);
    const [availablePackages, setAvailablePackages] = useState<any[]>([]);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [submittingPkgId, setSubmittingPkgId] = useState<string | null>(null);
    const [selectedPkg, setSelectedPkg] = useState<any>(null);
    const [transactionId, setTransactionId] = useState('');
    const [submittingPurchase, setSubmittingPurchase] = useState(false);
    const [adminUpiId, setAdminUpiId] = useState('admin@upi');
    const [showNearbyModal, setShowNearbyModal] = useState(false);
    const [nearbyEntry, setNearbyEntry] = useState<NearbyPlace>({ category: 'Schools', name: '', distance: '' });

    // Form Master State
    const [form, setForm] = useState<ProjectForm>({
        name: '', listingType: 'For Sale', propertyType: ['Residential'], subCategory: ['Flat/Apartment'], projectStatus: 'Under Construction', progressPercentage: 0, tagline: '', slug: '', isOurPick: false,
        location: '', address: '', city: '', state: 'Gujarat', country: 'India', pinCode: '', googleMapLocation: '', google3dViewLink: '', latitude: '', longitude: '',
        developerName: '', coDeveloper: '', developerLogo: '', developerWebsite: '', developerDescription: '', developerTagline: '', totalProjects: '0', projectsCompleted: '0', projectsOngoing: '0',
        projectsUpcoming: '0', projectsSince: '', aboutDeveloper: '', developerId: '',
        overviewConfiguration: '', overviewSizeRange: '', overviewPriceRange: '', overviewAvgPricePerSqFt: '', overviewProjectArea: '', overviewTotalPlotArea: '', overviewOpenSpacePercentage: '', overviewTotalTowers: '', overviewTotalFloors: '', overviewTotalUnits: '', overviewFloors: '',
        overviewAmenitiesIcon: '',
        towers: [],
        launchDate: '', possessionDate: '', timelineStatus: '', constructionStatusScore: 0,
        reraApproved: 'Yes', reraNumber: '', reraAuthority: 'Gujarat Real Estate Regulatory Authority', reraLink: '',
        shortDescription: '', fullDescription: '', 
        highlights: [
            { icon: 'fa-location-dot', text: 'Prime Location' },
            { icon: 'fa-city', text: 'Premium Residences' },
            { icon: 'fa-leaf', text: '65% Open Space' }
        ], 
        investmentPotential: '', videoUrl: '', brochure: null, brochureUrl: '', images: [], virtualTours: [{ title: '', link: '' }], youtubeVideos: [{ title: '', link: '' }],
       builderOffers: [{ offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }],
        faq: [{ question: '', answer: '' }],
        metaTitle: '', metaDescription: '', metaKeywords: '', canonicalUrl: '', focusKeyword: '',
        contactNumber: '', contactPhone: '', whatsappNumber: '', contactEmail: '', disclaimer: '',
        priceBreakup: [{ label: 'Basic Cost', value: '' }, { label: 'Extra Charges', value: '' }, { label: 'GST / Taxes', value: '' }],
        paymentPlans: [{ title: 'Possession Linked', description: 'Pay 25% now, rest on possession' }],
        bankApprovals: [], tpImage: '', tpImageUrl: '',
        builderTrustScore: 0, localityTrustScore: 0, loadingScore: 0, legalTrustScore: 0, amenitiesScore: 0, specificationsScore: 0,
        status: 'Pending', nearbyPlaces: [], dynamicSlots: [],
        sectionInfo: {
            overview: '',
            amenities: '',
            specifications: '',
            location: '',
            floorPlans: '',
            gallery: '',
            developer: '',
            nearby: '',
            about: '',
            highlights: '',
            faq: '',
            reviews: ''
        }
    })

    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false)

    // Dynamic Fields
    const [additionalDetails, setAdditionalDetails] = useState<AdditionalDetail[]>([{ label: '', value: '', icon: '' }])
    const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([{
        unitType: '',
        subUnits: [{ subUnitType: '', subUnitVariant: '', price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '' }]
    }])
    const [inventory, setInventory] = useState<InventoryItem[]>([{ tower: '', floor: '', unitNo: '', unitType: '', configuration: '', size: '', price: '', status: 'Available' }])
    const [specifications, setSpecifications] = useState<Specification[]>([])
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([])
    const [globalAmenities, setGlobalAmenities] = useState<{ _id: string, name: string, icon: string, score: number }[]>([])
    const [globalSpecs, setGlobalSpecs] = useState<{ _id: string, name: string, icon: string, details: string }[]>([])
    const [globalDetails, setGlobalDetails] = useState<{ _id: string, label: string, icon: string }[]>([])
    const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([{ title: '', image: '', imagePreview: '' }])
    const [developers, setDevelopers] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([])
    const [allLocalities, setAllLocalities] = useState<any[]>([])
    const [filteredLocalities, setFilteredLocalities] = useState<any[]>([])
    const [imagePreviews, setImagePreviews] = useState<string[]>([])
    const [galleryPreviews, setGalleryPreviews] = useState<GalleryState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [existingGallery, setExistingGallery] = useState<GalleryState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [galleryIcons, setGalleryIcons] = useState<Record<string, string>>({})
    const [scoreSettings, setScoreSettings] = useState<any>(null)

    // Media State
    const [featuredImage, setFeaturedImage] = useState<File | null>(null)
    const [featuredPreview, setFeaturedPreview] = useState('')
    const [tpImage, setTpImage] = useState<File | null>(null)
    const [tpPreview, setTpPreview] = useState('')
    const [gallery, setGallery] = useState<GalleryFileState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [brochureFile, setBrochureFile] = useState<File | null>(null)
    const [constructionStatusImage, setConstructionStatusImage] = useState<File | null>(null)
    const [constructionStatusPreview, setConstructionStatusPreview] = useState('')
    const [seoImageFile, setSeoImageFile] = useState<File | null>(null)
    const [seoImagePreview, setSeoImagePreview] = useState('')

    const handleGalleryUpload = (category: string, files: File[]) => {
        setGallery(prev => ({ ...prev, [category]: [...(prev[category] || []), ...files] }));
        const newPreviews = files.map(f => URL.createObjectURL(f));
        setGalleryPreviews(prev => ({ ...prev, [category]: [...(prev[category] || []), ...newPreviews] }));
    };

    const updateGalleryIcon = async (album: string, icon: File | string) => {
        if (typeof icon === 'string') {
            setGalleryIcons(prev => ({ ...prev, [album]: icon }));
        } else {
            try {
                const urls = await handleUpload([icon]);
                if (urls[0]) {
                    setGalleryIcons(prev => ({ ...prev, [album]: urls[0] }));
                }
            } catch (err) {
                toast.error("Failed to upload icon");
            }
        }
    };

    const removeGalleryCategory = (album: string) => {
        const newExGallery = { ...existingGallery };
        delete newExGallery[album];
        setExistingGallery(newExGallery);

        const newGallery = { ...gallery };
        delete newGallery[album];
        setGallery(newGallery);

        const newPreviews = { ...galleryPreviews };
        delete newPreviews[album];
        setGalleryPreviews(newPreviews);

        const newIcons = { ...galleryIcons };
        delete newIcons[album];
        setGalleryIcons(newIcons);
    };

    const removeGalleryImage = (category: string, idx: number) => {
        const existingCount = (existingGallery[category] || []).length;
        if (idx < existingCount) {
            setExistingGallery(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
            setGalleryPreviews(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
        } else {
            const adjustedIdx = idx - existingCount;
            setGallery(prev => ({ ...prev, [category]: (prev[category] || []).filter((_: File, i: number) => i !== adjustedIdx) }));
            setGalleryPreviews(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
        }
    };

    const syncStateWithProject = (data: any) => {
        if (!data) return;
        setForm({
            ...form,
            name: data.name || '',
            listingType: data.listingType || 'For Sale',
            propertyType: Array.isArray(data.propertyType) ? data.propertyType : (data.propertyType ? [data.propertyType] : []),
            subCategory: Array.isArray(data.subCategory) ? data.subCategory : (data.subCategory ? [data.subCategory] : []),
            projectStatus: data.projectStatus || 'Under Construction',
            progressPercentage: data.progressPercentage || 0,
            tagline: data.tagline || '',
            slug: data.slug || '',
            location: data.location || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || 'Gujarat',
            pinCode: data.pinCode || '',
            developerName: data.developerDetails?.name || '',
            developerId: data.developer || '',
            developerLogo: data.developerDetails?.logo || '',
            developerWebsite: data.developerDetails?.website || '',
            developerDescription: data.developerDetails?.description || '',
            developerTagline: data.developerDetails?.tagline || '',
            overviewConfiguration: data.overview?.configuration || '',
            overviewSizeRange: data.overview?.sizeRange || '',
            overviewTotalPlotArea: data.overview?.totalPlotArea || '',
            overviewPriceRange: data.overview?.priceRange || '',
            launchDate: data.launchDate ? new Date(data.launchDate).toISOString().split('T')[0] : '',
            possessionDate: data.possessionDate ? new Date(data.possessionDate).toISOString().split('T')[0] : '',
            reraApproved: data.reraApproved || 'Yes',
            shortDescription: data.shortDescription || '',
            fullDescription: data.description || '',
            videoUrl: data.videoUrl || '',
            brochureUrl: data.brochureUrl || '',
            virtualTours: data.virtualTours?.length > 0 ? data.virtualTours : [{ title: '', link: '' }],
            youtubeVideos: data.youtubeVideos?.length > 0 ? data.youtubeVideos : [{ title: '', link: '' }],
            contactPhone: data.contactPhone || '',
            whatsappNumber: data.whatsappNumber || '',
            contactEmail: data.contactEmail || '',
            status: data.status || 'Pending',
            sectionInfo: data.sectionInfo || {
                overview: '', amenities: '', specifications: '', location: '', floorPlans: '', gallery: '', developer: '', nearby: '', about: '', highlights: '', faq: '', reviews: ''
            }
        });

        if (data.gallery) {
            const eg: GalleryState = {
                exterior: data.gallery.exterior || [],
                interior: data.gallery.interior || [],
                amenities: data.gallery.amenities || [],
                construction: data.gallery.construction || []
            };
            Object.keys(data.gallery).forEach(key => {
                if (!['exterior', 'interior', 'amenities', 'construction'].includes(key)) {
                    eg[key] = data.gallery[key] || [];
                }
            });
            setExistingGallery(eg);

            const previews: GalleryState = { exterior: [], interior: [], amenities: [], construction: [] };
            Object.keys(eg).forEach(key => {
                previews[key] = eg[key].map((u: string) => getImageUrl(u));
            });
            setGalleryPreviews(previews);

            if (data.galleryIcons) {
                const icons: Record<string, string> = {};
                Object.keys(data.galleryIcons).forEach(k => {
                    icons[k] = data.galleryIcons[k].startsWith('http') ? getImageUrl(data.galleryIcons[k]) : data.galleryIcons[k];
                });
                setGalleryIcons(icons);
            }
        }

        if (data.featuredImage) setFeaturedPreview(getImageUrl(data.featuredImage));
        if (data.tpImage) setTpPreview(getImageUrl(data.tpImage));
        if (data.seoImageUrl) setSeoImagePreview(getImageUrl(data.seoImageUrl));

        // Reset temporary File upload variables
        setFeaturedImage(null);
        setTpImage(null);
        setSeoImageFile(null);
        setBrochureFile(null);
        setGallery({ exterior: [], interior: [], amenities: [], construction: [] });
    };

    const loadProfile = async () => {
        try {
            setLoadingProfile(true);
            const profile = await api.getUserProfile(token!);
            setUserProfile(profile);
            
            // packageExpiry null/undefined = Unlimited (no expiry)
            const hasActivePackage = !!(profile.assignedPackage) && 
                                   (!profile.packageExpiry || new Date(profile.packageExpiry) > new Date());

            if (!hasActivePackage) {
                const pkgs = await api.getPackages('broker');
                setAvailablePackages(pkgs);
                try {
                    const settings = await api.getSystemSettings();
                    if (settings?.adminUpiId) setAdminUpiId(settings.adminUpiId);
                } catch (e) {}
            }
        } catch (error) {
            console.error("Error loading broker profile:", error);
        } finally {
            setLoadingProfile(false);
        }
    };

    const openCheckout = (pkg: any) => {
        setSelectedPkg(pkg);
        setTransactionId('');
    };

    const closeCheckout = () => {
        setSelectedPkg(null);
        setTransactionId('');
    };

    const handleFreePurchase = async (pkg: any) => {
        if (!token) return;
        setSubmittingPkgId(pkg._id);
        try {
            const res = await api.assignPackage(token, pkg._id);
            const isSuccess = res && (
                res.success === true ||
                res._id ||
                res.assignedPackage ||
                (res.message && res.message.toLowerCase().includes('success')) ||
                (!res.error && !res.message?.toLowerCase().includes('error') && !res.message?.toLowerCase().includes('fail'))
            );
            if (isSuccess) {
                toast.success(`🎉 "${pkg.name}" package activated successfully! You can now add your project.`);
                await loadProfile();
            } else {
                toast.error(res?.message || 'Failed to activate package. Please try again.');
            }
        } catch (err) {
            toast.error('Server error. Could not activate free package.');
        } finally {
            setSubmittingPkgId(null);
        }
    };

    const handlePurchase = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token || !selectedPkg) return;
        if (!transactionId.trim()) {
            toast.error('Please enter a valid transaction reference number');
            return;
        }

        setSubmittingPurchase(true);
        try {
            await api.purchasePlan(token, {
                packageId: selectedPkg._id,
                name: selectedPkg.name,
                price: selectedPkg.price,
                duration: selectedPkg.period || 'Unlimited',
                users: '1',
                leadAccess: 'Broker Package',
                dailyUnlock: 'Unlimited',
                transactionId
            });
            toast.success('Purchase submitted successfully! Admin will review your transaction.');
            closeCheckout();
        } catch (err) {
            toast.error('Failed to submit purchase request');
        } finally {
            setSubmittingPurchase(false);
        }
    };

    const extractAmount = (priceStr: string) => {
        if (!priceStr) return '';
        const matches = priceStr.toString().match(/\d+/g);
        return matches ? matches.join('') : '';
    };

    const handlePackageInquiry = async (packageId: string) => {
        if (submittingPkgId) return;
        try {
            setSubmittingPkgId(packageId);
            const res = await api.sendPackageInquiry(token!, packageId);
            if (res.success) {
                toast.success("Inquiry sent! Our team will contact you to activate your broker package.");
            } else {
                toast.error(res.message || "Failed to send inquiry");
            }
        } catch (error) {
            console.error("Package inquiry error:", error);
            toast.error("Server connection error.");
        } finally {
            setSubmittingPkgId(null);
        }
    };

    const loadProjectData = async (projectId: string) => {
        try {
            const data = await api.getProjectById(projectId);
            if (!data) return;
            syncStateWithProject(data);
        } catch (err) { console.error(err); }
    };

    useEffect(() => {
        if (token) {
            loadProfile();
            if (id) loadProjectData(id);
            fetch(`${API_BASE}/developers`)
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        setDevelopers(data);
                        const defaultDev = data.find(d => 
                            d.name.toLowerCase().replace(/\s/g, '') === 'mypropertyguru'
                        );
                        if (defaultDev) {
                            setForm(p => ({
                                ...p,
                                developerId: defaultDev._id,
                                developerName: defaultDev.name,
                                developerLogo: defaultDev.logo || '',
                                developerWebsite: defaultDev.website || '',
                                developerDescription: defaultDev.description || '',
                                totalProjects: defaultDev.totalProjects || '0',
                                projectsCompleted: defaultDev.projectsCompleted || '0',
                                projectsOngoing: defaultDev.projectsOngoing || '0',
                                projectsUpcoming: defaultDev.projectsUpcoming || '0',
                                projectsSince: defaultDev.projectsSince || ''
                            }))
                        }
                    }
                });

            fetch(`${API_BASE}/amenities`).then(r => r.json()).then(data => { if (Array.isArray(data)) setGlobalAmenities(data) });
            fetch(`${API_BASE}/specifications`).then(r => r.json()).then(data => { if (Array.isArray(data)) setGlobalSpecs(data) });
            fetch(`${API_BASE}/project-details`).then(r => r.json()).then(data => { if (Array.isArray(data)) setGlobalDetails(data) });
            fetch(`${API_BASE}/cities`).then(r => r.json()).then(data => { if (Array.isArray(data)) setCities(data.filter((c: any) => c.isActive)) });
            fetch(`${API_BASE}/localities`).then(r => r.json()).then(data => { if (Array.isArray(data)) setAllLocalities(data.filter((l: any) => l.isActive)) });
            fetch(`${API_BASE}/localities`).then(r => r.json()).then(data => { if (Array.isArray(data)) setFilteredLocalities(data.filter((l: any) => l.isActive)) });
            fetch(`${API_BASE}/score-settings`).then(r => r.json()).then(setScoreSettings).catch(console.error);
        }
    }, [token, id]);

    useEffect(() => {
        if (isSlugManuallyEdited || !form.name) return;
        const slugify = (text: string) => {
            return text
                .toString()
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
                .replace(/^-+|-+$/g, '');
        };

        const namePart = slugify(form.name);
        const locationPart = form.location ? slugify(form.location) : '';
        const cityPart = form.city ? slugify(form.city) : '';

        const parts = [namePart, locationPart, cityPart].filter(Boolean);
        const newSlug = parts.join('-');

        setForm(prev => {
            if (prev.slug === newSlug) return prev;
            return { ...prev, slug: newSlug };
        });
    }, [form.name, form.location, form.city, isSlugManuallyEdited]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        if (name === 'propertyType') {
            const valStr = value as string;
            setForm(p => {
                const newTypes = p.propertyType.includes(valStr) 
                    ? p.propertyType.filter(t => t !== valStr) 
                    : [...p.propertyType, valStr];
                const validSubs = new Set<string>();
                newTypes.forEach(t => {
                    (SUBCATEGORIES[t] || []).forEach(s => validSubs.add(s));
                });
                const newSubs = p.subCategory.filter(s => validSubs.has(s));
                return { ...p, propertyType: newTypes, subCategory: newSubs };
            });
        } else if (name === 'subCategory') {
            const valStr = value as string;
            setForm(p => {
                const newSubs = p.subCategory.includes(valStr)
                    ? p.subCategory.filter(s => s !== valStr)
                    : [...p.subCategory, valStr];
                return { ...p, subCategory: newSubs };
            });
        } else if (name === 'city') {
            setForm(p => ({ ...p, city: value, location: '' }));
            setFilteredLocalities(allLocalities.filter(l => ((l.city && typeof l.city === 'object') ? l.city.name : l.city) === value));
        } else if (name === 'timelineStatus') {
            const options = scoreSettings?.constructionStatusOptions?.length > 0 
                ? scoreSettings.constructionStatusOptions 
                : DEFAULT_CONSTRUCTION_STATUS_OPTIONS;
            const option = options.find((o: any) => o.label === value);
            const getRequiredTitles = (status: string) => {
                if (status === 'RERA Approved') return ["Title Deed"];
                if (status === 'Under Construction' || status === 'Near Possession') return ["RERA Certificate", "Title Deed", "Approved Plan"];
                if (status === 'Ready to Move') return ["Occupancy Certificate", "Completion Certificate", "RERA Certificate"];
                return [];
            };
            const requiredTitles = getRequiredTitles(value);
            const currentTitles = legalDocuments.map(ld => ld.title);
            const newDocs = requiredTitles.filter(t => !currentTitles.includes(t)).map(t => ({ title: t, file: undefined, image: '', link: '', imagePreview: '' }));
            
            setForm(p => ({
                ...p,
                timelineStatus: value,
                constructionStatusScore: option ? option.score : 0
            }));
            
            if (newDocs.length > 0) {
                setLegalDocuments(prev => [...prev, ...newDocs]);
            }
        } else if (name === 'developerId') {
            const dev = developers.find(d => d._id === value);
            setForm(p => ({
                ...p,
                developerId: value,
                developerName: dev?.name || '',
                developerLogo: dev?.logo || '',
                developerWebsite: dev?.website || '',
                totalProjects: dev?.totalProjects || '0',
                projectsSince: dev?.projectsSince || ''
            }))
        } else if (name === 'slug') {
            setIsSlugManuallyEdited(true);
            setForm(p => ({ ...p, slug: value }));
        } else if (name === 'name') {
            setForm(p => ({ ...p, name: value }));
        } else {
            setForm(p => ({ ...p, [name]: val }))
        }
    }

    const handleUpload = async (files: File[]) => {
        if (!token || files.length === 0) return []
        const fd = new FormData();
        files.forEach(f => fd.append('images', f));
        const res = await fetch(`${API_BASE}/upload`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
        });
        const data = await res.json();
        return data.urls || [];
    }

    const handleSubmit = async (e?: React.FormEvent, statusOverride?: string) => {
        if (e) e.preventDefault();
        setSubmitting(true);
        try {
            const featuredUrl = featuredImage ? (await handleUpload([featuredImage]))[0] : form.images[0];
            const galleryUrls: Record<string, string[]> = {};
            const allCategories = Array.from(new Set([...Object.keys(existingGallery), ...Object.keys(gallery)]));
            for (const category of allCategories) {
                const existing = existingGallery[category] || [];
                const newlyUploaded = await handleUpload(gallery[category] || []);
                galleryUrls[category] = [...existing, ...newlyUploaded];
            }
            
            let brochureUrl = form.brochureUrl || '';
            if (brochureFile) {
                const fd = new FormData();
                fd.append('document', brochureFile);
                const res = await fetch(`${API_BASE}/upload/document`, {
                    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
                });
                const data = await res.json();
                brochureUrl = data.url || '';
            }

            let seoImageUrlStr = form.seoImageUrl || '';
            if (seoImageFile) {
                const urls = await handleUpload([seoImageFile]);
                seoImageUrlStr = urls[0] || seoImageUrlStr;
            }

            const updatedFloorPlans = await Promise.all(floorPlans.map(async fp => ({
                unitType: fp.unitType,
                subUnits: await Promise.all(fp.subUnits.map(async su => ({
                    subUnitType: su.subUnitType,
                    subUnitVariant: su.subUnitVariant,
                    price: su.price,
                    facing: su.facing,
                    reraArea: su.reraArea,
                    carpetArea: su.carpetArea,
                    builtUpArea: su.builtUpArea,
                    superBuiltUpArea: su.superBuiltUpArea,
                    details: su.details.filter(d => d.label),
                    image: su.imageFile ? (await handleUpload([su.imageFile]))[0] : su.image,
                    image2D: su.image2DFile ? (await handleUpload([su.image2DFile]))[0] : su.image2D,
                    image3D: su.image3DFile ? (await handleUpload([su.image3DFile]))[0] : su.image3D
                })))
            })));

            const payload = {
                ...form,
                virtualTours: form.virtualTours.filter(vt => vt.link),
                youtubeVideos: form.youtubeVideos.filter(yv => yv.link),
                status: statusOverride || 'Pending',
                postedByRole: 'broker',
                featuredImage: featuredUrl,
                gallery: galleryUrls,
                brochureUrl: brochureUrl,
                seoImageUrl: seoImageUrlStr,
                galleryIcons: galleryIcons,
                floorPlans: updatedFloorPlans,
                contactPhone: form.contactPhone,
                whatsappNumber: form.whatsappNumber,
                contactEmail: form.contactEmail,
                amenities: selectedAmenities,
                specifications: specifications.map(s => ({ category: s.category, details: s.details, icon: s.icon })),
                nearbyPlaces: form.nearbyPlaces,
                overview: {
                    configuration: form.overviewConfiguration,
                    sizeRange: form.overviewSizeRange,
                    projectArea: form.overviewProjectArea,
                    totalPlotArea: form.overviewTotalPlotArea,
                    openSpacePercentage: form.overviewOpenSpacePercentage,
                    totalTowers: form.overviewTotalTowers,
                    totalFloors: form.overviewTotalFloors,
                    totalUnits: form.overviewTotalUnits,
                    floors: form.overviewFloors
                }
            };

            const url = activeProjectId ? `${API_BASE}/projects/${activeProjectId}` : `${API_BASE}/projects`;
            const method = activeProjectId ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            });

            const responseData = await res.json();
            if (res.ok) {
                if (!activeProjectId && responseData._id) setActiveProjectId(responseData._id);
                syncStateWithProject(responseData);
                toast.success('Project published successfully!');
                navigate('/broker-my-projects');
            } else {
                toast.error(responseData.message || 'Submission failed');
            }
        } catch (err) { toast.error('Server error'); } finally { setSubmitting(false); }
    }

    if (loadingProfile) {
        return (
            <UserDashboardLayout>
                <div className="py-20 text-center">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-bold text-black uppercase tracking-widest text-xs">Loading Workspace...</p>
                </div>
            </UserDashboardLayout>
        );
    }

    const cleanTitle = (title: string) => {
        let cleaned = title.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
        const mapping: Record<string, string> = {
            'LISTINGS SYSTEM': 'CORE',
            'MEDIA SYSTEM': 'DISPLAY',
            'LOCATION + SEARCH': 'LOCALITY',
            'VISIBILITY SYSTEM': 'BOOST',
            'LEAD MGMT': 'ENGAGE',
            'ADVANCED FEATURES': 'PREMIUM'
        };
        for (const [key, value] of Object.entries(mapping)) {
            if (cleaned.toUpperCase().includes(key)) return value;
        }
        return cleaned.replace(/^\d+\.\s*/, '').replace(/\s*\(.*\)$/, '').trim().toUpperCase();
    };

    const cleanFeature = (text: string) => {
        return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
    };

    // packageExpiry null/undefined = Unlimited package (no expiry check needed)
    const hasActivePackage = !!(userProfile?.assignedPackage) && 
                           (!userProfile?.packageExpiry || new Date(userProfile.packageExpiry) > new Date());

    if (!hasActivePackage) {
        return (
            <UserDashboardLayout>
                <div className="mb-10 text-center max-w-2xl mx-auto py-10">
                    <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fa-solid fa-gem text-amber-500 text-3xl"></i>
                    </div>
                    <h2 className="text-3xl font-black text-black mb-4">Subscription Required</h2>
                    <p className="text-black font-medium">Please choose a package to start listing your premium projects.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto mb-10 pb-20">
                  {availablePackages.map((pkg, idx) => (
                    <div 
                      key={pkg.name || pkg._id} 
                      className={`group relative flex flex-col bg-white rounded-2xl border transition-all duration-700 hover:-translate-y-4 overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_50px_100px_-20px_rgba(30,58,138,0.2)] ${pkg.popular ? 'border-blue-500/30 ring-4 ring-blue-500/5' : 'border-blue-900/5'}`}
                    >
                      {/* Decorative Gradient Overlay */}
                      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -z-10 group-hover:bg-blue-500/10 transition-all duration-700"></div>

                      {/* Standard/Popular Tag */}
                      {pkg.popular && (
                        <div className="absolute top-8 right-[-50px] bg-gradient-to-r from-blue-600 to-blue-800 text-white text-[10px] font-bold py-2.5 px-14 rotate-45 uppercase tracking-[0.3em] shadow-2xl z-20">
                          Standard
                        </div>
                      )}

                      {/* Header Area */}
                      <div className="p-10 bg-blue-950 text-white relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-transparent"></div>
                        <div className="relative z-10">
                          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl shadow-2xl mb-8 group-hover:scale-110 transition-transform duration-500 bg-white/10 border border-white/20 backdrop-blur-md" style={{ color: pkg.color || '#3b82f6' }}>
                            <i className={`fa-solid ${pkg.icon || 'fa-star'}`}></i>
                          </div>
                          
                          <h3 className="text-3xl font-bold tracking-tight mb-2 text-white">{pkg.name}</h3>
                          <p className="text-[10px] font-medium text-blue-200/50 uppercase tracking-[4px] mb-8">{pkg.tagline}</p>
                          
                          <div className="flex items-baseline gap-2">
                            <span className="text-6xl font-bold tracking-tighter text-white">
                              ₹{pkg.price}
                            </span>
                            <span className="text-xs font-medium text-blue-200/30 uppercase tracking-[3px]">
                              / {pkg.period}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Features Area */}
                      <div className="p-10 flex-grow bg-white">
                        <div className="space-y-12">
                          {pkg.categories?.map((cat: any, ci: number) => (
                            <div key={ci} className="relative">
                              <h4 className="text-[11px] font-bold text-blue-950 uppercase tracking-[4px] mb-6 flex items-center gap-3">
                                 <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                                 {cleanTitle(cat.title)}
                              </h4>
                              <ul className="space-y-2 px-2">
                                {cat.features.map((feat: string, fi: number) => {
                                  const isAvailable = !feat.includes('❌') && !feat.toLowerCase().includes('no ');
                                  const cleanText = cleanFeature(feat);
                                  return (
                                    <li key={fi} className={`flex items-start gap-4 transition-all ${isAvailable ? 'text-blue-950' : 'opacity-20 grayscale'}`}>
                                      <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center border ${isAvailable ? 'bg-blue-50 border-blue-100 text-blue-600' : 'border-gray-100 text-gray-300'}`}>
                                        <i className={`fa-solid ${isAvailable ? 'fa-check' : 'fa-xmark'} text-[10px]`}></i>
                                      </div>
                                      <span className="text-[15px] font-medium leading-relaxed">
                                        {cleanText}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Footer Area */}
                      <div className="p-10 bg-gray-50/50 border-t border-gray-100">
                        {(Number(pkg.price) === 0) ? (
                          <button 
                            onClick={() => handleFreePurchase(pkg)}
                            disabled={submittingPkgId === pkg._id}
                            className="w-full py-6 rounded-2xl text-xs font-bold uppercase tracking-[4px] transition-all shadow-2xl active:scale-95 transform hover:scale-[1.02] relative overflow-hidden group/btn bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2"
                          >
                            {submittingPkgId === pkg._id ? (
                              <><i className="fa-solid fa-circle-notch fa-spin"></i> Activating...</>
                            ) : (
                              <><i className="fa-solid fa-bolt"></i> Get Started Free</>
                            )}
                          </button>
                        ) : (
                          <button 
                            onClick={() => openCheckout(pkg)}
                            className={`w-full py-6 rounded-2xl text-xs font-bold uppercase tracking-[4px] transition-all shadow-2xl active:scale-95 transform hover:scale-[1.02] relative overflow-hidden group/btn ${pkg.popular ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-950 text-white hover:bg-black'}`}
                          >
                            <div className="absolute inset-0 bg-white/10 -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
                            <span className="relative z-10">Purchase via UPI</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Checkout & QR Payment Modal */}
                <AnimatePresence>
                    {selectedPkg && (
                    <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
                        <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeCheckout}
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        />
                        <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-2xl bg-white rounded-xl p-6 shadow-2xl overflow-hidden"
                        >
                        <div className="flex justify-between items-center w-full mb-4">
                            <div>
                            <h2 className="text-2xl font-bold text-slate-900">Secure Payment</h2>
                            <p className="text-sm text-slate-500 mt-1">Pay via UPI to complete purchase</p>
                            </div>
                            <button
                            onClick={closeCheckout}
                            className="w-10 h-10 rounded-md bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-600 transition-all font-bold flex items-center justify-center"
                            >
                            <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <div className="flex flex-col md:flex-row gap-6">
                            {/* QR Code Section */}
                            <div className="md:w-1/2 bg-slate-50 border border-slate-200 rounded-xl p-5 flex flex-col items-center text-center">
                            <p className="text-sm font-bold text-slate-700 mb-2">Scan QR Code to Pay</p>
                            <div className="text-xs font-medium text-slate-500 mb-4 bg-white px-4 py-1.5 rounded-md border border-slate-200 shadow-sm select-all">
                                UPI ID: {adminUpiId}
                            </div>

                            <div className="w-36 h-36 bg-white p-2 border border-slate-200 rounded-xl shadow-sm mb-4 flex items-center justify-center">
                                <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(
                                    `upi://pay?pa=${adminUpiId}&pn=My%20Property%20Guru&am=${extractAmount(
                                    selectedPkg.price
                                    )}&cu=INR`
                                )}`}
                                alt="UPI QR Code"
                                className="w-full h-full object-contain"
                                />
                            </div>

                            <div className="flex items-center justify-center gap-2 mt-auto">
                                <span className="text-sm font-medium text-slate-500">Amount to pay:</span>
                                <span className="text-2xl font-black text-[#0F2A5F]">₹{extractAmount(selectedPkg.price)}</span>
                            </div>
                            </div>

                            {/* Form Section */}
                            <div className="md:w-1/2 flex flex-col justify-center">
                            <form onSubmit={handlePurchase} className="w-full space-y-4">
                                <div className="space-y-2">
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Transaction ID (UTR)</label>
                                <input
                                    type="text"
                                    required
                                    value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    placeholder="Enter 12-digit UTR Number"
                                    className="w-full px-4 py-3 rounded-md border border-slate-300 focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] outline-none bg-white text-slate-900 font-medium text-sm transition-all shadow-sm"
                                />
                                <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">After successful payment via the QR code or UPI ID, please enter the 12-digit transaction reference number (UTR) to authenticate your payment.</p>
                                </div>

                                <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeCheckout}
                                    className="flex-1 py-3.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-sm transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingPurchase}
                                    className="flex-1 py-3.5 rounded-md bg-[#0F2A5F] text-white hover:bg-black font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70"
                                >
                                    {submittingPurchase ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Submitting...</> : 'Submit Payment'}
                                </button>
                                </div>
                            </form>
                            </div>
                        </div>
                        </motion.div>
                    </div>
                    )}
                </AnimatePresence>
            </UserDashboardLayout>
        );
    }

    return (
        <UserDashboardLayout>
            <div className="max-w-7xl mx-auto py-8 disable-animations">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-6 px-4">
                    <div>
                        <h3 className="text-2xl font-bold text-black mb-1">Add Project Portfolio</h3>
                        <p className="text-black/60 text-[10px] font-black uppercase tracking-[3px]">Professional 8-block wizard for premium listing excellence.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/broker-my-projects" className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest text-black transition-all shadow-sm no-underline">
                            <i className="fa-solid fa-arrow-left"></i> My Projects
                        </Link>
                        <button 
                            type="button"
                            onClick={() => handleSubmit(undefined, 'Draft')}
                            className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest text-black transition-all shadow-sm"
                        >
                            <i className="fa-solid fa-floppy-disk"></i> Save as Draft
                        </button>
                    </div>
                </div>

                {/* Stepper Tabs Header */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 p-4 mx-4">
                    <div className="d-flex items-center gap-3 overflow-x-auto pb-3 tabs-scrollbar">
                        {BROKER_STEPS.map((s, idx) => {
                            const isCurrent = currentStep === idx + 1;
                            return (
                                <button
                                    type="button"
                                    key={idx}
                                    onClick={() => {
                                        setCurrentStep(idx + 1);
                                    }}
                                    className={`d-flex align-items-center gap-2 px-4 py-3 rounded-4 transition-all whitespace-nowrap border fw-black text-[10px] uppercase tracking-wider ${
                                        isCurrent 
                                            ? 'bg-[#D4A017] text-[#05144b] border-[#D4A017] shadow-sm' 
                                            : 'bg-light text-[#05144b] border-gray-200 hover:bg-gray-100 hover:text-[#05144b]'
                                    }`}
                                >
                                    <span className={`w-5 h-5 rounded-3 d-flex align-items-center justify-center text-[10px] fw-bold ${
                                        isCurrent ? 'bg-[#05144b] text-white' : 'bg-secondary/15 text-[#05144b]'
                                    }`}>
                                        {idx + 1}
                                    </span>
                                    <span>{s}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Stepper Header */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden mx-4">
                    <div className="p-4">
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center text-white font-bold shadow-lg shadow-black/20">{currentStep}</div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[10px] font-bold text-black/60 uppercase tracking-widest">Step {currentStep}</span>
                                    <h4 className="text-sm font-bold text-black italic leading-tight">{BROKER_STEPS[currentStep - 1]}</h4>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-base font-black text-black">{Math.round((currentStep / 9) * 100)}% Complete</span>
                            </div>
                        </div>
                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-black transition-all duration-700" style={{ width: `${(currentStep / 9) * 100}%` }}></div></div>
                    </div>
                </div>

                {/* Main Content Area Card */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-sm mb-8 mx-4 min-h-[600px]">
                    <div>
                        {currentStep === 1 && <Step1BasicInfo form={form} setForm={setForm} handleChange={handleChange} errors={errors} />}
                        {currentStep === 2 && <Step2Location form={form} setForm={setForm} handleChange={handleChange} errors={errors} cities={cities} filteredLocalities={filteredLocalities} />}
                        {currentStep === 3 && <Step3Developer form={form} setForm={setForm} handleChange={handleChange} developers={developers} errors={errors} />}
                        {currentStep === 4 && <Step4FloorPlan floorPlans={floorPlans} setFloorPlans={setFloorPlans} activeStep4Tab={activeStep4Tab} setActiveStep4Tab={setActiveStep4Tab} form={form} />}
                        {currentStep === 5 && <StepSEOTimeline form={form} setForm={setForm} handleChange={handleChange} errors={errors} seoImageFile={seoImageFile} setSeoImageFile={setSeoImageFile} seoImagePreview={seoImagePreview} setSeoImagePreview={setSeoImagePreview} />}
                        {currentStep === 6 && <Step8Media
                            form={form} setForm={setForm} handleChange={handleChange}
                            featuredPreview={featuredPreview} setFeaturedImage={setFeaturedImage} setFeaturedPreview={setFeaturedPreview}
                            tpPreview={tpPreview} setTpImage={setTpImage} setTpPreview={setTpPreview}
                            galleryPreviews={galleryPreviews} 
                            galleryIcons={galleryIcons}
                            handleGalleryUpload={handleGalleryUpload} 
                            updateGalleryIcon={updateGalleryIcon}
                            removeGalleryImage={removeGalleryImage} 
                            removeGalleryCategory={removeGalleryCategory}
                            brochureFile={brochureFile} setBrochureFile={setBrochureFile}
                            errors={errors}
                        />}
                        {currentStep === 7 && <Step8Grid
                            form={form}
                            setForm={setForm}
                            handleChange={handleChange}
                            errors={errors}
                            selectedAmenities={selectedAmenities}
                            setSelectedAmenities={setSelectedAmenities}
                            globalAmenities={globalAmenities}
                            specifications={specifications}
                            setSpecifications={setSpecifications}
                            legalDocuments={legalDocuments}
                            setLegalDocuments={setLegalDocuments}
                            globalLandmarkCategories={[]}
                        />}
                        {currentStep === 8 && <Step10Offers form={form} setForm={setForm} />}
                        {currentStep === 9 && <StepContactInfo form={form} setForm={setForm} handleChange={handleChange} />}
                    </div>

                    {/* Navigation Actions */}
                    <div className="flex items-center justify-between gap-4 mt-12 pt-6 border-t border-slate-100">
                        <button
                            type="button"
                            className="px-8 py-4 rounded-2xl bg-white border border-slate-200 text-black font-black text-xs flex items-center gap-3 transition-all shadow-sm"
                            onClick={() => currentStep === 1 ? navigate('/broker-my-projects') : setCurrentStep(s => Math.max(1, s - 1))}
                        >
                            <i className="fa-solid fa-arrow-left"></i> {currentStep === 1 ? 'BACK' : 'PREVIOUS'}
                        </button>
                        
                        {currentStep < 9 ? (
                            <button
                                type="button"
                                className="group px-8 py-4 rounded-2xl bg-black text-white hover:scale-105 active:scale-95 shadow-xl shadow-black/10 font-black text-sm transition-all flex items-center gap-3"
                                onClick={() => setCurrentStep(s => Math.min(9, s + 1))}
                            >
                                CONTINUE TO {BROKER_STEPS[currentStep]?.toUpperCase() || ''}
                                <i className="fa-solid fa-arrow-right"></i>
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => handleSubmit()}
                                disabled={submitting}
                                className="group px-10 py-4 bg-emerald-600 text-white rounded-2xl font-black text-sm transition-all flex items-center gap-3 hover:scale-105 active:scale-95 shadow-xl shadow-emerald-950/20 disabled:opacity-50"
                            >
                                {submitting ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-paper-plane"></i>}
                                {submitting ? 'SUBMITTING...' : 'PUBLISH PROJECT'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </UserDashboardLayout>
    );
}
